import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook to listen for match status changes via realtime
 * The actual status updates are now handled server-side via Edge Function cron job
 * This hook only subscribes to changes and invalidates queries
 */
export function useMatchStatusUpdater() {
  const queryClient = useQueryClient();
  const lastInvalidateRef = useRef<number>(0);

  useEffect(() => {
    const channel = supabase
      .channel('match-status-listener')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'matches',
          filter: 'status=neq.cancelled', // Only listen for non-cancelled matches
        },
        () => {
          // Debounce invalidation
          const now = Date.now();
          if (now - lastInvalidateRef.current > 2000) {
            queryClient.invalidateQueries({ queryKey: ['matches'] });
            queryClient.invalidateQueries({ queryKey: ['match'] });
            queryClient.invalidateQueries({ queryKey: ['my-matches'] });
            lastInvalidateRef.current = now;
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'match_results',
        },
        () => {
          // When results change, also refresh matches
          const now = Date.now();
          if (now - lastInvalidateRef.current > 2000) {
            queryClient.invalidateQueries({ queryKey: ['matches'] });
            queryClient.invalidateQueries({ queryKey: ['match'] });
            queryClient.invalidateQueries({ queryKey: ['my-matches'] });
            lastInvalidateRef.current = now;
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
}

/**
 * Helper function to get computed match status based on current time
 * Honours `duration_minutes` so longer matches don't auto-complete too early.
 */
export function getComputedMatchStatus(
  match: { date: string; time: string; status: string; match_format?: string | null; duration_minutes?: number | null }
): 'upcoming' | 'ongoing' | 'completed' | 'cancelled' {
  if (match.status === 'cancelled' || match.status === 'completed') {
    return match.status as 'cancelled' | 'completed';
  }

  const now = new Date();
  const matchDateTime = new Date(`${match.date}T${match.time}`);

  if (now >= matchDateTime) {
    const durationMs = (match.duration_minutes ?? 120) * 60 * 1000;
    const matchEnd = new Date(matchDateTime.getTime() + durationMs);
    if (now >= matchEnd) {
      return 'completed';
    }
    return 'ongoing';
  }

  return 'upcoming';
}
